/**
 * Custom events Intelligems (window.igEvents).
 * @see https://docs.intelligems.io/analytics/custom-events/custom-javascript-events
 *
 * Events à enregistrer dans l'Events Manager :
 * - fakeVariantClick_{productTitle}
 * - quantityBreakClick_{index}_{productTitle}
 * - quantityBreakAddToCart_{index}_{productTitle}
 *
 * Index 1-based. Les properties sont aussi envoyées
 * (stockées par Intelligems, pas encore filtrables en analytics).
 */

const MAX_EVENT_NAME_LENGTH = 100;

/**
 * @param {string} value
 * @returns {string}
 */
const sanitizeEventPart = (value) =>
  String(value || '')
    .replace(/[,'']/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * @param {string} name
 * @param {Record<string, unknown>} [properties]
 */
const trackIgEvent = (name, properties) => {
  const event = sanitizeEventPart(name).slice(0, MAX_EVENT_NAME_LENGTH);
  if (!event) return;

  window.igEvents = window.igEvents || [];

  /** @type {{ event: string, properties?: Record<string, unknown> }} */
  const payload = { event };
  if (properties && Object.keys(properties).length > 0) {
    payload.properties = properties;
  }

  window.igEvents.push(payload);
};

/**
 * @param {HTMLElement | null} root
 * @returns {{ productTitle: string, productId: string }}
 */
const getQuantityBreakProduct = (root) => {
  const el = root instanceof HTMLElement ? root : null;

  return {
    productTitle: el?.dataset?.igProductTitle?.trim() || '',
    productId: el?.dataset?.igProductId?.trim() || '',
  };
};

/**
 * @param {HTMLElement | null} item
 * @param {ParentNode | null} root
 * @returns {{ index: number, quantity: number, productTitle: string, productId: string } | null}
 */
const getQuantityBreakInfo = (item, root = document) => {
  if (!item) return null;

  const items = [...root.querySelectorAll('[data-ref="quantity-break-item"]')];
  const zeroBased = items.indexOf(item);
  if (zeroBased < 0) return null;

  const { productTitle, productId } = getQuantityBreakProduct(root instanceof HTMLElement ? root : item.closest('quantity-breaks'));

  return {
    index: zeroBased + 1,
    quantity: parseInt(item.dataset.quantity, 10) || 1,
    productTitle,
    productId,
  };
};

/**
 * @param {'quantityBreakClick' | 'quantityBreakAddToCart'} type
 * @param {{ index: number, quantity: number, productTitle: string, productId: string }} info
 */
const trackQuantityBreakEvent = (type, info) => {
  const suffix = info.productTitle ? `_${info.productTitle}` : '';
  trackIgEvent(`${type}_${info.index}${suffix}`, {
    index: info.index,
    quantity: info.quantity,
    productTitle: info.productTitle,
    productId: info.productId,
  });
};

/**
 * @param {Event} event
 */
const onDocumentClick = (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const fakeOption = target.closest('.fake-variant__option, .fake-variant-picker__variant');
  if (fakeOption instanceof HTMLElement) {
    const productTitle =
      fakeOption.dataset.igProductTitle?.trim() ||
      fakeOption.querySelector('.fake-variant__option-title')?.textContent?.trim() ||
      fakeOption.textContent?.trim() ||
      '';

    if (productTitle) {
      // Titre dans le nom d'event pour pouvoir l'utiliser comme objectif d'expérience
      trackIgEvent(`fakeVariantClick_${productTitle}`, { productTitle });
    }

    return;
  }

  const fakeCard = target.closest('.fake-variant-card');
  if (fakeCard instanceof HTMLElement && target.closest('a, button')) {
    const productTitle = fakeCard.dataset.igProductTitle?.trim() || '';
    if (productTitle) {
      trackIgEvent(`fakeVariantClick_${productTitle}`, { productTitle });
    }

    return;
  }

  const quantityBreak = target.closest('[data-ref="quantity-break-item"]');
  if (!(quantityBreak instanceof HTMLElement)) return;
  if (quantityBreak.classList.contains('quantity-break--selected')) return;
  if (quantityBreak.classList.contains('quantity-break--disabled')) return;

  const qbRoot = quantityBreak.closest('quantity-breaks') || document;
  const info = getQuantityBreakInfo(quantityBreak, qbRoot);
  if (!info) return;

  trackQuantityBreakEvent('quantityBreakClick', info);
};

/**
 * @param {CustomEvent} event
 */
const onCartUpdated = (event) => {
  if (event.detail?.data?.source !== 'product-form') return;

  const quantityBreaks = document.querySelector('quantity-breaks');
  if (!quantityBreaks) return;

  // ponytail: isActive peut manquer si le CE n'est pas encore défini — fallback classe CSS
  if (typeof quantityBreaks.isActive === 'function') {
    if (!quantityBreaks.isActive()) return;
  } else if (quantityBreaks.classList.contains('quantity-breaks--hidden')) {
    return;
  }

  const selected = quantityBreaks.querySelector('.quantity-break--selected');
  const info = getQuantityBreakInfo(selected, quantityBreaks);
  if (!info) return;

  trackQuantityBreakEvent('quantityBreakAddToCart', info);
};

document.addEventListener('click', onDocumentClick, true);
document.addEventListener('cart:updated', onCartUpdated);
